/**
 * Jotai atoms for Hanging Protocol state management
 *
 * Uses atomFamily pattern for per-slot isolation:
 * - Each slot has its own atom (no cross-slot interference)
 * - Scales to any number of slots
 * - Simpler updates (no need to spread entire state object)
 */

import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { atomFamily } from "jotai-family";

import { DEFAULT_WINDOWING } from "../constants/windowPresets";
import { SLOT_IDS, SLOT_CONFIGS, SlotId, DEFAULT_SLOT } from "../utils/hangingProtocolService";
import { emit as busEmit } from "../lib/viewerBus";
import type { PlaneOrientation } from "../viewport/types";

// =============================================================================
// Types
// =============================================================================

export type VolumeStatus = "pending" | "loading" | "ready" | "error";

/** Layout mode for the hanging protocol grid.
 *  - "seg-2up": view-dependent segmentation layout — large axial on the left,
 *    a single switchable companion (coronal⇄sagittal) top-right, and the 3D
 *    model bottom-right. Default for `view_dependent` projects (see
 *    HangingProtocolGrid's default-init effect); only ever offered there. */
export type LayoutMode = "2x2" | "2x1" | "single" | "seg-2up";

/** Layout mode atom - persisted to localStorage */
export const layoutModeAtom = atomWithStorage<LayoutMode>("hangingProtocolLayout", "2x2");

/** Which plane fills the top-right companion pane in the "seg-2up" layout.
 *  The third (non-companion) plane is simply not mounted, so this also decides
 *  which planes nnInteractive seeds. Persisted; default Coronal (renal default). */
export type CompanionPlane = "Coronal" | "Sagittal";
export const companionPlaneAtom = atomWithStorage<CompanionPlane>(
  "hangingProtocolCompanionPlane",
  "Coronal",
);

/** Reference lines enabled - shows scout lines on perpendicular views */
export const referenceLinesEnabledAtom = atomWithStorage("referenceLinesEnabled", true);

/**
 * A pane rendering `sourceSeriesId`'s volume resliced to `plane` — an MPR
 * companion of that series, not a series of its own. `plane` is the same
 * `PlaneOrientation` union the whole render path is typed on (Axial included)
 * — the menus that OFFER a reformat are what exclude a series' own native
 * plane (see `derivePlane`-based filtering in the menu components), not this
 * type.
 */
export interface ResliceTarget {
  sourceSeriesId: string;
  plane: PlaneOrientation;
}

export interface LoadingState {
  isLoading: boolean;
  loadedFrames: number;
  totalFrames: number;
}

export interface SlotState {
  seriesId: string | null;
  // Set when this slot renders a REFORMAT (MPR) of another series' volume
  // rather than a series of its own. `seriesId` stays NULL on a reformat slot:
  // finding-jump (selectFinding), phase slice-sync (useHangingProtocolSync),
  // the drop swap, and the volume LRU all key off `seriesId`, and treating a
  // reformat pane as the source axial's pane makes them write axial list
  // indices into reslice index space — a confidently wrong slice. The render
  // path reaches the source through this field instead
  // (HangingProtocolGrid passes `resliceTarget.sourceSeriesId` down as the
  // pane's seriesId prop).
  reslice?: ResliceTarget | null;
  // The slot's current slice — single source of truth (list-index space).
  // Every input writes this directly: wheel (AnatomicScrollTool), scrollbar
  // drag, ↑/↓ keys (scrollSliceAtom), AI chip jump, initial-load. The
  // `externalSliceIndex` effect in VolumeViewport reads this and drives
  // cornerstone via IPP-based `jumpToWorld`. Cornerstone's `VOLUME_NEW_IMAGE`
  // writes back idempotently when the resolved list-index differs (covers
  // cornerstone-autonomous moves like the reference-lines tool).
  sliceIndex: number;
  totalSlices: number;
  windowing: { center: number; width: number };
  // What Reset restores W/L to. Set on series-assign (DICOM W/L) and on
  // each explicit preset pick (SET_ALL_WINDOWING). Drag-windowing does
  // NOT touch this.
  initialWindowing: { center: number; width: number };
  zoom: number;
  // Pan offset (mirror of Cornerstone camera). Updated by CAMERA_MODIFIED.
  pan: [number, number];
  loadingState: LoadingState;
  isInverted: boolean;
  // Load-time-only cross-series jump target (e.g. a finding-jump to a series
  // not yet displayed). Seeded by SET_SERIES, consumed in exactly two places —
  // resolveStackStartIndex (the weak-GPU stack's setStack start index) and a
  // single promote-into-`sliceIndex` at the volume-ready gate that immediately
  // clears it. NOT a render-time value: the viewport always renders
  // `sliceIndex`, so counter, scrollbar, and image cannot desync.
  pendingJump: number | null;
  // Per-series list-index direction in patient coords (from IPP). +1 means
  // increasing list-index moves anatomically up (cranial for axial, anterior
  // for coronal, patient-right for sagittal); -1 means the opposite; 0 means
  // unknown / fall back to default mapping. Routed by wheel, scrollbar, and
  // ↑/↓ so all three agree on "up" for any volume regardless of imageIds
  // sort order. See utils/anatomicScroll.ts.
  listAnatomicSign: -1 | 0 | 1;
}

// =============================================================================
// Default State Factory
// =============================================================================

const createDefaultLoadingState = (): LoadingState => ({
  isLoading: false,
  loadedFrames: 0,
  totalFrames: 0,
});

export const createDefaultSlotState = (): SlotState => ({
  seriesId: null,
  reslice: null,
  sliceIndex: 0,
  totalSlices: 0,
  windowing: { ...DEFAULT_WINDOWING },
  initialWindowing: { ...DEFAULT_WINDOWING },
  zoom: 1,
  pan: [0, 0],
  loadingState: createDefaultLoadingState(),
  isInverted: false,
  pendingJump: null,
  listAnatomicSign: 0,
});

// =============================================================================
// Per-Slot State (atomFamily)
// =============================================================================

/**
 * Per-slot state atom family.
 * Each slot gets its own isolated atom - no cross-slot interference.
 *
 * Usage:
 *   const [slotState, setSlotState] = useAtom(slotStateFamily(slotId));
 */
export const slotStateFamily = atomFamily<string, ReturnType<typeof atom<SlotState>>>(() =>
  atom<SlotState>(createDefaultSlotState()),
);

// =============================================================================
// Per-Series Volume Status (atomFamily)
// =============================================================================

/**
 * Volume loading status per series.
 * Tracks whether a volume is pending, loading, ready, or errored.
 */
export const volumeStatusFamily = atomFamily<
  string | null,
  ReturnType<typeof atom<VolumeStatus | undefined>>
>(() => atom<VolumeStatus | undefined>(undefined));

// =============================================================================
// Shared/Coordination State (regular atoms)
// =============================================================================

/** Which slot is currently active (for keyboard navigation, highlighting) */
export const activeSlotAtom = atom<string>(DEFAULT_SLOT);

/** Source pixels currently displayable in a slot, including MPR source identity. */
export const displayedSourceFamily = atomFamily<
  string,
  ReturnType<typeof atom<{ studyInstanceUid: string; seriesInstanceUid: string } | null>>
>(() => atom<{ studyInstanceUid: string; seriesInstanceUid: string } | null>(null));

/** Which slot is maximized (spotlight mode), null if none */
export const maximizedSlotAtom = atom<string | null>(null);

// =============================================================================
// Priority Series Loading (load findings series first)
// =============================================================================

/** Raw priority series ID from model prediction (may not be in any slot) */
const rawPrioritySeriesIdAtom = atom<string | null>(null);

/**
 * Derived: effective priority series ID (only if actually assigned to a slot).
 * Returns null if raw priority isn't in any slot, preventing deadlock.
 */
export const prioritySeriesIdAtom = atom((get) => {
  const rawPriority = get(rawPrioritySeriesIdAtom);
  if (!rawPriority) return null;

  // Check if priority series is assigned to any slot
  for (const slotId of SLOT_IDS) {
    if (get(slotStateFamily(slotId)).seriesId === rawPriority) {
      return rawPriority;
    }
  }
  return null; // Not in any slot - no priority gating
});

/**
 * Write-only: set priority series for loading, optionally spotlight its slot.
 * spotlight=true (default): also maximize and activate the slot (for model predictions).
 * spotlight=false: only set loading priority without changing layout (for axial-first fallback).
 */
export const setPrioritySeriesAtom = atom(
  null,
  (get, set, payload: string | null | { seriesId: string | null; spotlight?: boolean }) => {
    const seriesId =
      typeof payload === "object" && payload !== null && "seriesId" in payload
        ? payload.seriesId
        : payload;
    const spotlight =
      typeof payload === "object" && payload !== null && "seriesId" in payload
        ? payload.spotlight !== false
        : true;

    set(rawPrioritySeriesIdAtom, seriesId);

    if (!seriesId || !spotlight) return;

    // Find and spotlight the slot containing priority series
    for (const slotId of SLOT_IDS) {
      if (get(slotStateFamily(slotId)).seriesId === seriesId) {
        set(maximizedSlotAtom, slotId);
        set(activeSlotAtom, slotId);
        return;
      }
    }
  },
);

/** Registry of all slot IDs currently in use (for bulk operations) */
export const registeredSlotsAtom = atom<Set<string>>(new Set<string>());

// =============================================================================
// Convenience Actions (write-only atoms for common operations)
// =============================================================================

/**
 * Toggle maximize state for a slot
 */
export const toggleMaximizeAtom = atom(null, (get, set, slotId: string) => {
  const current = get(maximizedSlotAtom);
  set(maximizedSlotAtom, current === slotId ? null : slotId);
  set(activeSlotAtom, slotId);
});

/**
 * Toggle invert state for a slot
 */
export const toggleInvertAtom = atom(null, (get, set, slotId: string) => {
  const current = get(slotStateFamily(slotId));
  set(slotStateFamily(slotId), { ...current, isInverted: !current.isInverted });
});

/**
 * Scroll slice up or down for the active slot. "Up" is anatomic up — driven
 * by the slot's IPP-derived `listAnatomicSign` so cranial is always toward
 * the top regardless of how the volume's imageIds happen to be sorted. Falls
 * back to "up = sliceIndex - 1" when the sign is unknown (sign = 0).
 */
export const scrollSliceAtom = atom(null, (get, set, direction: "up" | "down") => {
  const activeSlot = get(activeSlotAtom);
  const current = get(slotStateFamily(activeSlot));
  const { sliceIndex, totalSlices, listAnatomicSign } = current;

  if (totalSlices === 0) return;

  // sign = +1 → increasing list-index = anatomic-up   → "up" increments.
  // sign = -1 → decreasing list-index = anatomic-up   → "up" decrements.
  // sign =  0 → no IPP signal → fall back to default ("up" decrements).
  const upStep = listAnatomicSign === 1 ? +1 : -1;
  const step = direction === "up" ? upStep : -upStep;
  const newIndex = Math.max(0, Math.min(totalSlices - 1, sliceIndex + step));

  if (newIndex !== sliceIndex) {
    set(slotStateFamily(activeSlot), { ...current, sliceIndex: newIndex });
  }
});

/**
 * Reset a slot's view (W/L → initialWindowing, zoom/pan/invert defaults).
 * Slice position is PRESERVED (#358). Emits 'resetSlotView' on the bus so
 * ProtocolViewport calls Cornerstone resetCamera({resetZoom, resetPan});
 * the windowing-prop effect picks up the new windowing value and pushes voiRange.
 */
export const resetSlotViewAtom = atom(null, (get, set, slotId: string) => {
  const current = get(slotStateFamily(slotId));
  set(slotStateFamily(slotId), {
    ...current,
    windowing: { ...current.initialWindowing },
    zoom: 1,
    pan: [0, 0],
    isInverted: false,
  });
  busEmit("resetSlotView", { slotId });
});

// =============================================================================
// Legacy Compatibility Layer
// =============================================================================

// These types and atoms maintain backward compatibility with existing code
// that uses the old dispatch pattern. Can be removed once migration is complete.

// Re-export SlotId for backward compatibility (now imported from hangingProtocolService)
export type { SlotId };

export interface HangingProtocolState {
  slots: Record<SlotId, SlotState>;
  activeSlot: SlotId;
  maximizedSlot: SlotId | null;
  volumeStatus: Record<string, VolumeStatus>;
}

/**
 * Derived atom that assembles the old state shape from atomFamily atoms.
 * Used for backward compatibility during migration.
 */
export const hangingProtocolAtom = atom<HangingProtocolState>((get) => {
  const slots = {} as Record<SlotId, SlotState>;
  const volumeStatus: Record<string, VolumeStatus> = {};

  SLOT_IDS.forEach((slotId) => {
    const slotState = get(slotStateFamily(slotId));
    slots[slotId] = slotState;

    // Collect volume status for each series
    if (slotState.seriesId) {
      const status = get(volumeStatusFamily(slotState.seriesId));
      if (status) {
        volumeStatus[slotState.seriesId] = status;
      }
    }
  });

  return {
    slots,
    activeSlot: get(activeSlotAtom) as SlotId,
    maximizedSlot: get(maximizedSlotAtom) as SlotId | null,
    volumeStatus,
  };
});

// Legacy action types for backward compatibility
export type HangingProtocolAction =
  | { type: "RESET" }
  | {
      type: "INITIALIZE";
      assignments: Record<string, string | null>;
      seriesMetadata: Record<
        string,
        { window_center?: number | null; window_width?: number | null }
      >;
      instanceCounts: Record<string, number>;
    }
  | { type: "CHANGE_SLICE"; slotId: string; sliceIndex: number }
  | { type: "CHANGE_WINDOWING"; slotId: string; center: number; width: number }
  | { type: "SET_ALL_WINDOWING"; center: number; width: number }
  | {
      type: "SET_SERIES";
      slotId: string;
      seriesId: string;
      /** Optional jump target — seeds pendingJump so the slot lands on
       *  this slice once the new series loads (e.g. a cross-series finding-jump). */
      sliceIndex?: number;
      windowing?: { center: number; width: number };
      isInverted?: boolean;
    }
  | { type: "SET_ACTIVE"; slotId: string }
  | { type: "SET_TOTAL_SLICES"; slotId: string; total: number }
  | { type: "SET_ZOOM"; slotId: string; zoom: number }
  | { type: "TOGGLE_MAXIMIZE"; slotId: string }
  | {
      type: "SET_LOADING_STATE";
      slotId: string;
      isLoading: boolean;
      loadedFrames: number;
      totalFrames: number;
    }
  | { type: "SET_VOLUME_STATUS"; seriesId: string; status: VolumeStatus };

/**
 * Legacy action dispatcher for backward compatibility.
 * Routes actions to the appropriate atomFamily atoms.
 */
export const hangingProtocolActionsAtom = atom(null, (get, set, action: HangingProtocolAction) => {
  switch (action.type) {
    case "RESET": {
      // Use imported SLOT_IDS constant
      SLOT_IDS.forEach((slotId) => {
        set(slotStateFamily(slotId), createDefaultSlotState());
      });
      set(activeSlotAtom, DEFAULT_SLOT);
      set(maximizedSlotAtom, null);
      break;
    }

    case "INITIALIZE": {
      const { assignments, instanceCounts } = action;

      SLOT_IDS.forEach((slotId) => {
        const seriesId = assignments[slotId] ?? null;
        const totalSlices = seriesId ? (instanceCounts[seriesId] ?? 0) : 0;

        // Always open at the standard (soft-tissue) window, ignoring the
        // DICOM-embedded W/L, so every study opens to a predictable, consistent
        // contrast rather than whatever the scanner/PACS baked in. The user can
        // still switch presets or drag W/L after load; Reset returns here.
        const windowing = { ...DEFAULT_WINDOWING };

        // Coronal/sagittal start at middle slice
        const slotConfig = SLOT_CONFIGS[slotId];
        const isCoronalOrSagittal =
          slotConfig.plane === "Coronal" || slotConfig.plane === "Sagittal";
        const sliceIndex = isCoronalOrSagittal && totalSlices > 0 ? Math.floor(totalSlices / 2) : 0;

        set(slotStateFamily(slotId), {
          ...createDefaultSlotState(),
          seriesId,
          totalSlices,
          sliceIndex,
          windowing,
          initialWindowing: { ...windowing },
        });
      });

      set(activeSlotAtom, DEFAULT_SLOT);
      set(maximizedSlotAtom, null);
      break;
    }

    case "CHANGE_SLICE": {
      const { slotId, sliceIndex } = action;
      const current = get(slotStateFamily(slotId));
      // Same-series jump: write the authoritative slice directly. Cross-series
      // jumps (series not yet loaded) go through SET_SERIES, which seeds
      // `pendingJump` instead.
      set(slotStateFamily(slotId), { ...current, sliceIndex });
      break;
    }

    case "CHANGE_WINDOWING": {
      const { slotId, center, width } = action;
      const current = get(slotStateFamily(slotId));
      set(slotStateFamily(slotId), { ...current, windowing: { center, width } });
      break;
    }

    case "SET_ALL_WINDOWING": {
      const { center, width } = action;
      SLOT_IDS.forEach((slotId) => {
        const current = get(slotStateFamily(slotId));
        set(slotStateFamily(slotId), {
          ...current,
          windowing: { center, width },
          initialWindowing: { center, width },
        });
      });
      break;
    }

    case "SET_SERIES": {
      const { slotId, seriesId, windowing, isInverted, sliceIndex: targetSliceIndex } = action;
      // Fall back to DEFAULT_WINDOWING rather than the prior slot's
      // windowing. The seeding effect in ProtocolViewport overrides this
      // when the new series's DICOM W/L hydrates; until then a Reset
      // hitting DEFAULT (40, 400) is a generic safe default, whereas the
      // prior value could be actively wrong (lung W/L on abdomen CT).
      const nextWindowing = windowing ?? { ...DEFAULT_WINDOWING };
      set(slotStateFamily(slotId), {
        ...createDefaultSlotState(),
        seriesId,
        sliceIndex: 0,
        totalSlices: 0,
        // Seed the jump target synchronously so a cross-series finding-jump
        // lands on the finding's slice once the new series loads (resolveStackStartIndex
        // honors pendingJump for the stack start index; the volume-ready gate
        // promotes it into sliceIndex). Replaces a requestAnimationFrame'd
        // CHANGE_SLICE in selectFinding that raced StackViewport's setStack and
        // made the jump land on the middle slice on the weak-GPU fallback.
        pendingJump: typeof targetSliceIndex === "number" ? targetSliceIndex : null,
        windowing: nextWindowing,
        initialWindowing: { ...nextWindowing },
        isInverted: isInverted ?? false,
      });
      break;
    }

    case "SET_ACTIVE": {
      set(activeSlotAtom, action.slotId);
      break;
    }

    case "SET_TOTAL_SLICES": {
      const { slotId, total } = action;
      const current = get(slotStateFamily(slotId));
      set(slotStateFamily(slotId), { ...current, totalSlices: total });
      break;
    }

    case "SET_ZOOM": {
      const { slotId, zoom } = action;
      const current = get(slotStateFamily(slotId));
      set(slotStateFamily(slotId), { ...current, zoom });
      break;
    }

    case "TOGGLE_MAXIMIZE": {
      const { slotId } = action;
      const current = get(maximizedSlotAtom);
      set(maximizedSlotAtom, current === slotId ? null : slotId);
      set(activeSlotAtom, slotId);
      break;
    }

    case "SET_LOADING_STATE": {
      const { slotId, isLoading, loadedFrames, totalFrames } = action;
      const current = get(slotStateFamily(slotId));
      set(slotStateFamily(slotId), {
        ...current,
        loadingState: { isLoading, loadedFrames, totalFrames },
      });
      break;
    }

    case "SET_VOLUME_STATUS": {
      const { seriesId, status } = action;
      set(volumeStatusFamily(seriesId), status);
      break;
    }
  }
});

// =============================================================================
// Selectors (for backward compatibility)
// =============================================================================

/**
 * Get all slot assignments as a record (for backward compatibility)
 */
export const slotAssignmentsSelector = atom((get) => {
  return Object.fromEntries(
    SLOT_IDS.map((id) => [id, get(slotStateFamily(id)).seriesId]),
  ) as Record<SlotId, string | null>;
});

/**
 * Get volume status for a specific series (for backward compatibility)
 */
export const volumeStatusSelector = (seriesId: string | null) =>
  atom((get) => {
    if (!seriesId) return undefined;
    return get(volumeStatusFamily(seriesId));
  });

/**
 * Snapshot of (seriesId, sliceIndex) for every slot. Consumers (e.g. the
 * measure-pin overlay) can match a saved (series, slice) pair against the
 * currently-displayed slots without depending on slot count or order.
 *
 * Deliberately raw `seriesId` — NO `reslice.sourceSeriesId` fallback. The
 * measure-pin consumer (`CaseViewer.tsx`'s `pinSliceVisible`) compares this
 * `sliceIndex` against `measureClickPin.maskSliceIndices`, which the backend
 * computes against the SOURCE series' own volume (`series_uid: seriesId` in
 * `useMeasureClickWiring`'s request) — i.e. that series' native index space.
 * A reformat pane's `sliceIndex` is in RESLICE index space instead (the
 * reoriented plane's index), so a fallback here would compare two numbers
 * that mean different things and could match or miss by coincidence — the
 * same aliasing class the Critical fix was about, just in the measure-pin
 * path instead of finding-jump. The reformat pane genuinely cannot answer
 * "am I showing a slice this axial-space mask covers", so `seriesId: null`
 * (never matches) is the correct, honest answer, not a regression.
 */
export const allSlotSeriesSlicesSelector = atom((get) =>
  SLOT_IDS.map((id) => {
    const s = get(slotStateFamily(id));
    return { slotId: id, seriesId: s.seriesId, sliceIndex: s.sliceIndex };
  }),
);
